// Phase 2 — DataTable. TanStack headless table + shadcn Table (docs/04 §4.1).
//
// IMPORTANT coexistence contract: search and pagination are presentation only.
// `onSearch` fires the SAME action the screen already dispatches (server-side
// page fetch), and pagination state stays owned by the screen. This component
// never changes payloads — it only restyles the controls.
import * as React from "react";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, Download, Rows2, Rows3, Search } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "ui/table";
import { Input } from "ui/input";
import { Button } from "ui/button";
import { Skeleton } from "ui/skeleton";
import { EmptyState } from "./empty-state";
import { cn } from "lib/utils";
import { downloadCsv, CsvColumn } from "./csv-export";

// Opt-in per-column "frozen" support: set `meta: { sticky: "left" | "right" }`
// on a ColumnDef to pin it while the rest of the table scrolls horizontally.
// Read via `as any` since TanStack's default ColumnMeta is an empty interface
// and this app doesn't declare a global module augmentation for it.
// Multiple columns can be pinned on the same side (e.g. ID + Name) — each one
// is offset by the measured width of the sticky columns "above" it on that
// side (see useStickyOffsets below), and only the outermost one (the one
// bordering the scrollable columns) gets the divider border.
type StickySide = "left" | "right";

function getStickySide(columnDef: unknown): StickySide | undefined {
  return (columnDef as { meta?: { sticky?: StickySide } })?.meta?.sticky;
}

function stickyClass(columnDef: unknown, kind: "header" | "cell", isEdge: boolean) {
  const sticky = getStickySide(columnDef);
  if (!sticky) return undefined;
  return cn(
    "sticky",
    isEdge && (sticky === "left" ? "border-r" : "border-l"),
    kind === "header" ? "z-20 bg-muted" : "z-10 bg-background"
  );
}

// Measures the rendered width of each pinned column (via its header cell,
// which shares column width with every body cell) and returns a px offset
// per column id so stacked sticky columns line up left-to-right (or
// right-to-left) instead of all sitting at 0 and overlapping.
function useStickyOffsets(leftIds: string[], rightIds: string[]) {
  const cellRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const [offsets, setOffsets] = React.useState<Record<string, number>>({});

  const measure = React.useCallback(() => {
    const next: Record<string, number> = {};
    let acc = 0;
    for (const id of leftIds) {
      next[id] = acc;
      acc += cellRefs.current[id]?.offsetWidth ?? 0;
    }
    acc = 0;
    for (let i = rightIds.length - 1; i >= 0; i--) {
      const id = rightIds[i];
      next[id] = acc;
      acc += cellRefs.current[id]?.offsetWidth ?? 0;
    }
    setOffsets((prev) => {
      const ids = Object.keys(next);
      const same = ids.length === Object.keys(prev).length && ids.every((id) => prev[id] === next[id]);
      return same ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftIds.join(","), rightIds.join(",")]);

  React.useLayoutEffect(() => {
    measure();
  });

  React.useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const setCellRef = React.useCallback((id: string) => (el: HTMLElement | null) => {
    cellRefs.current[id] = el;
  }, []);

  return { offsets, setCellRef };
}

export interface DataTablePagination {
  /** 1-based current page. */
  page: number;
  /** Total record count (server total). */
  total: number;
  pageSize?: number;
  onChange: (page: number) => void;
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Controlled search text (optional). */
  searchValue?: string;
  /** Debounced; must dispatch the SAME action the screen uses today. */
  onSearch?: (query: string) => void;
  searchDebounce?: number;
  pagination?: DataTablePagination;
  /** Extra toolbar content (filters, sort dropdowns) rendered on the right. */
  toolbar?: React.ReactNode;
  /** Screen title, rendered in the SAME row as search/toolbar (saves vertical space). */
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Primary action(s) (e.g. +Add), rendered alongside the toolbar on the right. */
  headerActions?: React.ReactNode;
  emptyState?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Extra className for a given row (e.g. a temporary highlight), merged into the row's own classes. */
  getRowClassName?: (row: TData) => string | undefined;
  /**
   * Row to auto-scroll-into-view (centered) and visually highlight once it
   * appears in `data` — e.g. the record a user arrived at via an Audit Log
   * deep link. Requires `getRowKey` to identify which row matches.
   */
  highlightRowKey?: string;
  /** Derives the comparable key for `highlightRowKey` from a row's data. */
  getRowKey?: (row: TData) => string | undefined;
  /** Render a card per row on mobile. Falls back to a header/value list. */
  mobileCard?: (row: TData) => React.ReactNode;
  className?: string;
  /**
   * Fill the height of a bounded flex parent instead of capping at 70vh.
   * Use inside layouts (e.g. a Sheet) that need the pagination footer to stay
   * on-screen without the page having to scroll past the row list first.
   */
  fillHeight?: boolean;
  /** Hide the comfortable/compact row-density toggle button. */
  hideDensityToggle?: boolean;
  /**
   * Renders a "Download CSV" button in the toolbar row. Exports `rows`
   * (defaults to the `data` prop — pass the full, unpaginated list explicitly
   * when `data` only holds the current page) using `columns` (defaults to
   * this table's own `columns`, keyed by column `id` with its `header` as the
   * CSV column title — only works automatically when `header` is a plain
   * string, not a custom cell/JSX header).
   */
  csvExport?: {
    fileName: string;
    rows?: TData[];
    columns?: CsvColumn[];
  };
}

function useDebouncedCallback(cb: ((v: string) => void) | undefined, delay: number) {
  const ref = React.useRef<ReturnType<typeof setTimeout>>();
  return React.useCallback(
    (v: string) => {
      if (ref.current) clearTimeout(ref.current);
      ref.current = setTimeout(() => cb?.(v), delay);
    },
    [cb, delay]
  );
}

export function DataTable<TData>({
  columns,
  data,
  loading = false,
  searchable = false,
  searchPlaceholder = "Search…",
  searchValue,
  onSearch,
  searchDebounce = 400,
  pagination,
  toolbar,
  title,
  description,
  headerActions,
  emptyState,
  onRowClick,
  getRowClassName,
  highlightRowKey,
  getRowKey,
  mobileCard,
  className,
  fillHeight = false,
  hideDensityToggle = false,
  csvExport,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [density, setDensity] = React.useState<"comfortable" | "compact">("comfortable");
  const [search, setSearch] = React.useState(searchValue ?? "");
  const debouncedSearch = useDebouncedCallback(onSearch, searchDebounce);

  React.useEffect(() => {
    if (searchValue !== undefined) setSearch(searchValue);
  }, [searchValue]);

  // Scrolls the matched row to the center of the viewport the moment it
  // shows up in `data` (e.g. after an Audit Log deep-link's own search
  // resolves) — refs are attached per-render below since which row (if any)
  // matches can change every time `data` changes.
  const highlightedRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const highlightedCardRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!highlightRowKey) return;
    (highlightedRowRef.current || highlightedCardRef.current)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightRowKey, data]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // pages come from the server; we only render controls
  });

  const pageSize = pagination?.pageSize ?? 10;
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pageSize)) : 1;
  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;

  const csvExportRows = csvExport?.rows ?? data;
  const csvExportColumns: CsvColumn[] =
    csvExport?.columns ??
    columns
      .map((c) => ({ key: String((c as any).id ?? ""), header: typeof c.header === "string" ? c.header : "" }))
      .filter((c) => c.key && c.header);
  const handleCsvExport = () => {
    if (!csvExport) return;
    downloadCsv(csvExport.fileName, csvExportRows, csvExportColumns);
  };

  // const csvExportRows = csvExport?.rows ?? data;
  // const csvExportColumns: CsvColumn[] =
  //   csvExport?.columns ??
  //   columns
  //     .map((c) => ({ key: String((c as any).id ?? ""), header: typeof c.header === "string" ? c.header : "" }))
  //     .filter((c) => c.key && c.header);
  // const handleCsvExport = () => {
  //   if (!csvExport) return;
  //   downloadCsv(csvExport.fileName, csvExportRows, csvExportColumns);
  // };

  const leafColumns = table.getAllLeafColumns();
  const leftStickyIds = leafColumns.filter((c) => getStickySide(c.columnDef) === "left").map((c) => c.id);
  const rightStickyIds = leafColumns.filter((c) => getStickySide(c.columnDef) === "right").map((c) => c.id);
  const lastLeftStickyId = leftStickyIds[leftStickyIds.length - 1];
  const firstRightStickyId = rightStickyIds[0];
  const { offsets: stickyOffsets, setCellRef } = useStickyOffsets(leftStickyIds, rightStickyIds);
  const stickyStyle = (columnId: string, side: StickySide | undefined) =>
    side ? { [side]: stickyOffsets[columnId] ?? 0 } : undefined;

  return (
    <div className={cn("mz flex flex-col gap-3", fillHeight && "min-h-0", className)}>
      {/* Toolbar — title, search, filters and actions share a single row to save
          vertical space (docs/09 UI feedback: stack this the same way the
          Inventory toolbar does, instead of a separate title row). */}
      {(title || searchable || toolbar || headerActions || csvExport) && (
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
          {title && (
            <div className="min-w-0 shrink-0">
              <h1 className="truncate text-xl font-semibold text-foreground">{title}</h1>
              {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between lg:flex-1 lg:justify-end">
            {searchable ? (
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  placeholder={searchPlaceholder}
                  className="pl-9"
                  aria-label="Search"
                />
              </div>
            ) : (
              <span />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {toolbar}
              {headerActions}
              {csvExport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCsvExport}
                  disabled={csvExportRows.length === 0}
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </Button>
              )}
              {!hideDensityToggle && (
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={density === "comfortable" ? "Switch to compact rows" : "Switch to comfortable rows"}
                  onClick={() => setDensity((d) => (d === "comfortable" ? "compact" : "comfortable"))}
                >
                  {density === "comfortable" ? <Rows2 className="h-4 w-4" /> : <Rows3 className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table (md and up) */}
      <div
        className={cn(
          "hidden overflow-auto rounded-xl border md:block",
          fillHeight ? "flex-1 min-h-0" : "max-h-[70vh]"
        )}
      >
        <Table className={cn(density === "compact" && "[&_td]:py-1.5 [&_th]:h-9")}>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const side = getStickySide(header.column.columnDef);
                  const isEdge = side === "left" ? header.column.id === lastLeftStickyId : side === "right" ? header.column.id === firstRightStickyId : false;
                  return (
                    <TableHead
                      key={header.id}
                      ref={side ? setCellRef(header.column.id) : undefined}
                      className={stickyClass(header.column.columnDef, "header", isEdge)}
                      style={stickyStyle(header.column.id, side)}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 uppercase tracking-wide outline-none hover:text-foreground focus-visible:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : sorted === "desc" ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <TableRow key={`sk-${r}`} className="hover:bg-transparent">
                  {table.getAllLeafColumns().map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton className="h-4 w-full max-w-[120px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : hasRows ? (
              rows.map((row) => {
                const isHighlighted = !!highlightRowKey && getRowKey?.(row.original) === highlightRowKey;
                return (
                  <TableRow
                    key={row.id}
                    ref={isHighlighted ? highlightedRowRef : undefined}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      "even:bg-muted/30",
                      onRowClick && "cursor-pointer",
                      isHighlighted && "bg-primary/10 hover:bg-primary/15 ring-1 ring-inset ring-primary/40",
                      getRowClassName?.(row.original)
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const side = getStickySide(cell.column.columnDef);
                      const isEdge = side === "left" ? cell.column.id === lastLeftStickyId : side === "right" ? cell.column.id === firstRightStickyId : false;
                      return (
                        <TableCell
                          key={cell.id}
                          className={stickyClass(cell.column.columnDef, "cell", isEdge)}
                          style={stickyStyle(cell.column.id, side)}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={table.getAllLeafColumns().length} className="p-0">
                  {emptyState ?? <EmptyState />}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards (below md) */}
      <div
        className={cn(
          "flex flex-col gap-3 md:hidden",
          fillHeight && "flex-1 min-h-0 overflow-auto"
        )}
      >
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={`mc-${i}`} className="h-24 w-full rounded-xl" />)
        ) : hasRows ? (
          rows.map((row) => {
            const isHighlighted = !!highlightRowKey && getRowKey?.(row.original) === highlightRowKey;
            return (
            <div
              key={row.id}
              ref={isHighlighted ? highlightedCardRef : undefined}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={cn(
                "rounded-xl border p-4",
                onRowClick && "cursor-pointer",
                isHighlighted && "bg-primary/10 ring-1 ring-primary/40",
                getRowClassName?.(row.original)
              )}
            >
              {mobileCard ? (
                mobileCard(row.original)
              ) : (
                <dl className="grid grid-cols-[minmax(0,40%)_1fr] gap-x-3 gap-y-1.5 text-sm">
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      <dt className="truncate text-xs uppercase tracking-wide text-muted-foreground">
                        {flexRender(cell.column.columnDef.header, cell.getContext() as any)}
                      </dt>
                      <dd className="min-w-0 text-foreground">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </div>
            );
          })
        ) : (
          <div className="rounded-xl border">{emptyState ?? <EmptyState />}</div>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="shrink-0">
          <Pagination
            page={pagination.page}
            totalPages={totalPages}
            onChange={pagination.onChange}
          />
        </div>
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const [goto, setGoto] = React.useState("");
  const submitGoto = () => {
    const n = parseInt(goto, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= totalPages) onChange(n);
    setGoto("");
  };
  return (
    <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
      <p className="text-sm text-muted-foreground">
        Page <span className="font-medium text-foreground tabular-nums">{page}</span> of{" "}
        <span className="font-medium text-foreground tabular-nums">{totalPages}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground">Go to</span>
          <Input
            value={goto}
            onChange={(e) => setGoto(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && submitGoto()}
            onBlur={submitGoto}
            inputMode="numeric"
            aria-label="Go to page"
            className="h-9 w-16 text-center"
          />
        </div>
      </div>
    </div>
  );
}
